import clsx from "clsx";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ContentDivider } from "@atoms/contentDivider";
import { Icon } from "@atoms/icon";
import { Typography } from "@atoms/typography";
import { FastPassword } from "@components/fastFields/fastPassword/fastPassword";
import { Row } from "@layout/row";
import { PasswordStrength } from "@molecules/passwordStrength";
import { FormLayout } from "@organisms/formLayout";
import {
  resetPasswordFormInitialValues,
  resetPasswordFormValidationSchema,
} from "@templates/authManagement/resetPasswordForm/schema";
import { zodResolver } from "@utils/zod";

import classes from "./styles.module.scss";

import type { IFormTemplateProps } from "@interfaces";
import type { IResetPasswordFormSchema } from "@templates/authManagement/resetPasswordForm/schema";

export function ResetPasswordForm({
  isLoading,
  onSubmit,
  initialValues,
}: IFormTemplateProps<IResetPasswordFormSchema>) {
  const { t } = useTranslation("page");

  const { control, handleSubmit, watch } = useForm<IResetPasswordFormSchema>({
    defaultValues: initialValues || resetPasswordFormInitialValues,
    resolver: zodResolver(resetPasswordFormValidationSchema),
    mode: "onChange",
  });

  return (
    <div className={clsx(classes.loginForm)}>
      <div className={clsx(classes.loginForm__head)}>
        <div className={clsx(classes.loginForm__circleOutside)}>
          <div className={clsx(classes.loginForm__circle)}>
            <Icon name={"KeyLine"} fontSize={"fs-32"} color={"icon-sub-600"} />
          </div>
        </div>
        <Typography fontType={"label-x-large"}>
          {t("auth.resetPassword.title")}
        </Typography>
        <Typography fontType={"paragraph-medium"} themeColor={"text-sub-600"}>
          {t("auth.resetPassword.enterPassword")}
        </Typography>
      </div>
      <ContentDivider className={"w-100"} />
      <FormLayout
        noCancel
        submitButtonProps={{ type: "resetPassword", className: "w-100" }}
        onSubmit={handleSubmit(onSubmit)}
        isLoading={isLoading}
        noBack
        noMarginTop
      >
        <Row noBorder>
          <Row.Input>
            <FastPassword
              type={"password"}
              control={control}
              name={"newPassword"}
              isRequired
            />
          </Row.Input>
          <Row.Input>
            <FastPassword
              type={"confirmNewPassword"}
              control={control}
              name={"confirmNewPassword"}
              isRequired
            />
          </Row.Input>
          <Row.Input>
            <PasswordStrength password={watch("newPassword")} />
          </Row.Input>
        </Row>
      </FormLayout>
    </div>
  );
}
